// AccountFormModal — create or edit an investment account.
import { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCOUNT_TYPES = ['Super', 'Trust', 'Individual', 'Joint', 'Company', 'SMSF', 'Foundation', 'Other'];
const CURRENCIES = ['AUD', 'USD', 'EUR', 'GBP', 'NZD', 'JPY', 'CHF', 'CAD', 'HKD', 'SGD'];

export default function AccountFormModal({ clientId, initial = {}, onClose, onSaved }) {
  const { token } = useAuth();
  const [form, setForm] = useState({
    name: initial.name || '',
    custodian: initial.custodian || '',
    accountType: initial.accountType || '',
    accountNumber: initial.accountNumber || '',
    currency: initial.currency || 'AUD',
    entityId: initial.entityId || '',
    status: initial.status || 'active',
    notes: initial.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Account name is required'); return; }
    setSaving(true);
    try {
      const url = initial.id
        ? `${API}/investment-accounts/${initial.id}`
        : `${API}/clients/${clientId}/investment-accounts`;
      const res = await fetch(url, {
        method: initial.id ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(await res.text());
      onSaved?.();
    } catch (e) {
      logger.warn('AccountFormModal save failed', e);
      toast.error('Could not save account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="account-modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E0D9]">
          <h3 className="font-heading text-lg text-[#2C2420]">{initial.id ? 'Edit account' : 'New investment account'}</h3>
          <button onClick={onClose} className="text-[#5D524D] hover:text-[#2C2420]" data-testid="account-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <Field label="Account name *">
            <Input value={form.name} onChange={set('name')} placeholder="e.g. Smith Family Trust — Macquarie Wrap" data-testid="account-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Custodian"><Input value={form.custodian} onChange={set('custodian')} placeholder="Macquarie, CommSec…" data-testid="account-custodian" /></Field>
            <Field label="Account type">
              <select value={form.accountType} onChange={set('accountType')} className="w-full h-9 border border-[#E6E0D9] rounded-md px-2 text-sm" data-testid="account-type">
                <option value="">Select…</option>
                {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Account number"><Input value={form.accountNumber} onChange={set('accountNumber')} data-testid="account-number" /></Field>
            <Field label="Currency">
              <select value={form.currency} onChange={set('currency')} className="w-full h-9 border border-[#E6E0D9] rounded-md px-2 text-sm" data-testid="account-currency">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Notes">
            <textarea value={form.notes} onChange={set('notes')} rows={2} className="w-full border border-[#E6E0D9] rounded-md px-2 py-1.5 text-sm" data-testid="account-notes" />
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-[#E6E0D9] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="account-modal-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white" data-testid="account-modal-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            {initial.id ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs text-[#5D524D] mb-1 inline-block">{label}</span>
      {children}
    </label>
  );
}
